import React, {
  MutableRefObject,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  SyntheticEvent,
} from "react";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import CardBillingQueueUX from "./CardBillingQueueUX";
import CardPatientSearchBox from "react-lib/apps/HISV3/TPD/CardPatientSearchBox";
import ButtonLockExpense from "./ButtonLockExpense";

import DateTextBox from "react-lib/apps/common/DateTextBox";
import { Button, Icon, Input, Label } from "semantic-ui-react";
import ModReceiptCode from "./ModReceiptCode";
import { useInterval } from "react-lib/utils/hooksUtils";
import moment from "moment";
// Config
import CONFIG from "config/config";
import { useIntl } from "react-intl";

const CARD_BILLING_QUEUE = "CardBillingQueue";

const POLLING_PATIENT_QUEUE = 1000 * 120;

const CardBillingQueue = (props: any) => {
  const intl = useIntl();
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const touchCountRef = useRef(0);
  const touchTimerRef = useRef<any>(null);
  const timestampSearchRef = useRef<any>(moment());

  useEffect(() => {
    props.onEvent({
      message: "BillingQueueAction",
      params: {
        action: "initial",
        card: CARD_BILLING_QUEUE,
      },
    });
  }, [props.selectedDevice]);

  useInterval(() => {
    const diff = moment().diff(timestampSearchRef.current, "milliseconds");
    const half = POLLING_PATIENT_QUEUE / 2;

    if (diff >= half) {
      props.onEvent({
        message: "BillingQueueAction",
        params: {
          action: "search",
          card: CARD_BILLING_QUEUE,
        },
      });
    }
  }, POLLING_PATIENT_QUEUE);

  const handleDoubleClick = useCallback((rowInfo: any) => {
    const { id } = rowInfo?.original;
    if (id) {
      props.onEvent({
        message: "BillingQueueAction",
        params: {
          action: "select_encounter",
          encounterId: id,
          card: CARD_BILLING_QUEUE,
        },
      });
      props?.forward();
    }
  }, []);

  const handleClickCheckExpenses = useCallback(
    (item: any) => (e: SyntheticEvent) => {
      e.stopPropagation();

      handleDoubleClick({ original: item });
    },
    [handleDoubleClick]
  );

  const handleSearch = useCallback(() => {
    props.onEvent({
      message: "BillingQueueAction",
      params: {
        action: "search",
        card: CARD_BILLING_QUEUE,
      },
    });
  }, []);

  // UseMemo
  const encounterStatusOptions = useMemo(() => {
    return [
      { key: "ทั้งหมด", value: "ทั้งหมด", text: "ทั้งหมด" },
      ...(props.masterOptions?.EncounterStatus || []),
      ...(props.masterOptions?.encounterIPDStatus || []).filter((item: any) =>
        [
          "Admit",
          "Ward Admitted",
          "หมอมีคำสั่งให้ Discharge",
          "Cashier Request",
          "Cashier Discharged",
          "กลับบ้าน",
          "ส่งตัวไป OR",
        ].includes(item.text)
      ),
    ];
  }, [props.masterOptions?.EncounterStatus]);

  const billingQueueList = useMemo(() => {
    return props.billingQueueList?.map((item) => ({
      ...item,
      number: (
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr" }}>
          <ButtonLockExpense
            onEvent={props.onEvent}
            setProp={props.setProp}
            encounterId={item.id}
            status={item.locker_general_status}
            patientName={item.patient_name}
            hn={item.hn}
            number={item.number}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            onSuccess={handleSearch}
          />
          <div style={{ textAlign: "center" }}>{item.number}</div>
        </div>
      ),
      type: (
        <div style={{ display: "flex" }}>
          <div>{item.encounter_type}</div>
          {item.is_telemed && (
            <Label circular color="yellow" style={{ height: "1.8rem", width: "1.8rem" }}>
              <Icon name="phone" style={{ transform: "rotate(90deg)" }} />
            </Label>
          )}
        </div>
      ),
      action: (
        <div style={{ display: "grid", placeContent: "center" }}>
          <Button color="green" size="mini" onClick={handleClickCheckExpenses(item)}>
            {intl.formatMessage({ id: "ตรวจสอบค่าใช้จ่าย" })}
          </Button>
        </div>
      ),
    }));
  }, [handleClickCheckExpenses, props.billingQueueList, props.buttonLoadCheck, props.errorMessage]);

  const handleChangeChecked = (name: string, checked: boolean) => {
    // console.log("handleChangeChecked name: ", name, event)
    props.onEvent({
      message: "BillingQueueAction",
      params: {
        action: "change_value",
        name,
        value: checked,
        card: CARD_BILLING_QUEUE,
      },
    });
  };

  const handleChangePatient = (id: any, hn: string, full_name: string) => {
    // console.log("handleChangePatient: ", id, hn, full_name)
    props.onEvent({
      message: "BillingQueueAction",
      params: {
        action: "change_value",
        name: "selectedPatient",
        value: { id, hn, full_name },
        card: CARD_BILLING_QUEUE,
      },
    });
  };

  const handleChangeDate = (name: string) => (date: string) => {
    props.onEvent({
      message: "BillingQueueAction",
      params: {
        action: "change_value",
        name,
        value: date,
        card: CARD_BILLING_QUEUE,
      },
    });
    // console.log("name: ", name, " date: ", date)
  };

  const handleChangeOption = (event: any, data: any) => {
    props.onEvent({
      message: "BillingQueueAction",
      params: {
        action: "change_value",
        name: data.name,
        value: data.value,
        card: CARD_BILLING_QUEUE,
      },
    });
  };

  const handleTouchStart = (rowInfo: any) => {
    touchCountRef.current++;

    if (touchCountRef.current === 1) {
      touchTimerRef.current = setTimeout(function () {
        touchCountRef.current = 0;
      }, 300);
    } else if (touchCountRef.current === 2) {
      clearTimeout(touchTimerRef.current);
      touchCountRef.current = 0;

      // handleDoubleClick(rowInfo);
    }
  };

  // console.log("CardBilling Queue: ", props)
  return (
    <>
      <SnackMessage
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_BILLING_QUEUE}`, null);
          props.setProp(`successMessage.${CARD_BILLING_QUEUE}`, null);
        }}
        error={props.errorMessage?.[CARD_BILLING_QUEUE]}
        success={props.successMessage?.[CARD_BILLING_QUEUE]}
        languageUX={props.languageUX}
      />
      <CardBillingQueueUX
        // loading
        loadingData={props.loadingData}
        // checekd
        billingFilterQueue={props.billingFilterQueue}
        // options
        masterOptions={props.masterOptions}
        encounterStatusOptions={encounterStatusOptions}
        // value
        billingQueueList={billingQueueList}
        // function
        onChangeChecked={handleChangeChecked}
        onChangeOption={handleChangeOption}
        onSearch={handleSearch}
        selectedQueue={(state: any, rowInfo: any, column: any, instance: any) => {
          return {
            className:
              rowInfo?.original?.id && rowInfo?.original?.id === selectedRow?.id
                ? "blueSelectedRow"
                : "",
            onClick: () => {
              setSelectedRow(rowInfo?.original);
            },
            onTouchStart: () => handleTouchStart(rowInfo),
            // onDoubleClick: () => handleDoubleClick(rowInfo),
          };
        }}
        // component
        patientSearchBox={
          <CardPatientSearchBox
            controller={props.drugOrderQueueController}
            defaultValue={props.billingFilterQueue?.selectedPatient.hn}
            onEnterPatientSearch={handleChangePatient}
            languageUX={props.languageUX}
          />
        }
        receiptStartDate={
          <DateTextBox
            value={props.billingFilterQueue?.receiptStartDate}
            onChange={handleChangeDate("receiptStartDate")}
          />
        }
        receiptEndDate={
          <DateTextBox
            value={props.billingFilterQueue?.receiptEndDate}
            onChange={handleChangeDate("receiptEndDate")}
          />
        }
        pagination={
          <Pagination
            page={props.billingQueuePagination?.page}
            total={props.billingQueuePagination?.totalPage}
            onPrevious={() => {
              props.onEvent({
                message: "BillingQueuePagination",
                params: { action: "previous" },
              });
            }}
            onEnter={(page: string) => {
              if (Number.isInteger(parseInt(page))) {
                props.onEvent({
                  message: "BillingQueuePagination",
                  params: { action: "page_change", value: page },
                });
              }
            }}
            onNext={() => {
              props.onEvent({
                message: "BillingQueuePagination",
                params: { action: "next" },
              });
            }}
          />
        }
        languageUX={props.languageUX}
      />

      {!props.constanceConfig.BIL_AUTO_RUN_RECEIPT && (
        <ModReceiptCode
          onEvent={props.onEvent}
          setProp={props.setProp}
          // data
          bilReceiptCodeDetail={props.bilReceiptCodeDetail}
          stationName={props.stationName}
          // CommonInterface
          successMessage={props.successMessage}
          errorMessage={props.errorMessage}
          languageUX={props.languageUX}
        />
      )}
    </>
  );
};

const paginationStyle = {
  flexCenter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  fontPadding: {
    fontSize: "16px",
    padding: "0px 3px",
  },
  inputStyle: {
    width: "60px",
    fontSize: "16px",
  },
};

const Pagination = (props: any) => {
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    setPage(props.page);
  }, [props.page]);

  return (
    <div style={paginationStyle.flexCenter}>
      <Icon name="caret left" size="big" onClick={props.onPrevious} />
      <Input
        style={paginationStyle.inputStyle}
        value={page}
        onChange={(event: any) => {
          setPage(event.target.value);
        }}
        onKeyDown={(event: any, data: any) => {
          console.log("Event keydown: ", event.key, page);
          if (event.key === "Enter") {
            props.onEnter(page);
          }
        }}
      />
      <span style={paginationStyle.fontPadding}>/</span>
      <span style={paginationStyle.fontPadding}>{props.total || 1}</span>
      <Icon name="caret right" size="big" onClick={props.onNext} />
    </div>
  );
};
export default CardBillingQueue;
